import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Typography } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import DistanceSourceTagPanel from './DistanceSourceTagPanel';
import { VARIANCE_FLAG_PCT } from './constants';
import { formatKm, formatPercent, formatReadingKm } from './format';

const { Title, Text } = Typography;

const SPACE_BETWEEN = 'space-between';

/**
 * Row notes. Each row gets at most one: the reason a value is missing when it is missing,
 * or a qualifier on the value when it is present. The two can never both apply, and
 * rendering "— (partial trail) (no GPS pings recorded)" says both at once.
 */
const gpsNote = (distances, gps) => {
	// "Partial trail" qualifies a real distance. With no distance there is no trail to
	// call partial, so the reason for the gap is the only thing worth saying.
	if (distances.gpsKm === null) return 'no GPS pings recorded';
	return gps.isPartialTrail ? 'partial trail' : null;
};

const varianceNote = (distances) => {
	if (distances.variancePct === null) {
		// Three different reasons collapse to the same null and need different notes: a
		// value is genuinely missing, the odometer distance is exactly 0 (the pre-existing
		// division guard), or both values exist but GPS coverage was too low for the
		// comparison to mean anything (resolveVariance's coverage gate) - mirror that same
		// condition here rather than threading a separate reason through the view model.
		if (
			distances.gpsKm === null ||
			distances.odometerKm === null ||
			distances.odometerKm <= 0
		) {
			return 'needs both a GPS and an odometer distance';
		}
		return 'GPS coverage insufficient to compare';
	}
	return distances.varianceFlagged
		? `above the ${VARIANCE_FLAG_PCT}% review threshold`
		: `within the ${VARIANCE_FLAG_PCT}% threshold`;
};

/**
 * Shows whichever readings exist, so a trip with a start but no close reads
 * "1,708 → —" rather than the flatly untrue "readings not captured".
 */
const odometerNote = (distances) => {
	if (distances.odometerStart === null && distances.odometerEnd === null) {
		return 'readings not captured';
	}
	return `${formatReadingKm(distances.odometerStart)} → ${formatReadingKm(
		distances.odometerEnd
	)}`;
};

/**
 * "Distance Reconciliation" — every measurement taken of this trip on the left, the verdict
 * on the right.
 *
 * Rows are data, not markup, so a row with nothing to show carries the *reason* it is empty
 * instead of a bare dash. "Odometer not captured" is a fixable finding; a blank cell is not.
 */
function ReconciliationTable({ model }) {
	const { distances, gps } = model;
	const { threshold } = distances;

	const rows = [
		{
			key: 'planned',
			label: 'Planned Distance',
			value: formatKm(distances.plannedKm),
			note: distances.plannedKm === null ? 'no planned distance on this route' : null,
		},
		{
			key: 'gps',
			label: 'GPS Tracked Distance',
			value: formatKm(distances.gpsKm),
			note: gpsNote(distances, gps),
		},
		{
			key: 'coverage',
			label: 'GPS Coverage %',
			value: formatPercent(gps.coveragePercent),
			note: gps.coveragePercent === null ? 'not measured yet' : null,
		},
		{
			key: 'odometer',
			label: 'Odometer Distance',
			value: formatKm(distances.odometerKm),
			note: odometerNote(distances),
			tone: distances.odometerInvalid ? 'error' : null,
			warning: distances.odometerInvalid
				? 'End reading is lower than the start reading — check the closing entry.'
				: null,
		},
		{
			key: 'variance',
			label: 'Variance (GPS vs Odometer)',
			value: formatPercent(distances.variancePct),
			tone: distances.varianceFlagged ? 'error' : null,
			note: varianceNote(distances),
		},
		{
			key: 'threshold',
			label: 'Threshold Distance',
			value: formatKm(threshold.distanceKm),
			note:
				threshold.basisKm !== null
					? `Planned ${formatKm(threshold.basisKm)} + ${threshold.bufferPct}%`
					: 'needs a planned distance',
		},
	];

	const thresholdPill = {
		within: { label: 'Within Threshold', color: '#15803d', background: '#f0fdf4' },
		exceeds: { label: 'Exceeds Threshold', color: '#b91c1c', background: '#fff1f0' },
	}[threshold.status];

	return (
		<Card
			bodyStyle={{ padding: '24px 28px' }}
			style={{ borderRadius: 14, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: SPACE_BETWEEN,
					alignItems: 'center',
					gap: 16,
					marginBottom: 8,
				}}
			>
				<Title level={5} style={{ margin: 0, fontWeight: 700, fontSize: 17 }}>
					Distance Reconciliation
				</Title>
				{distances.varianceFlagged && (
					<span
						style={{
							background: '#fff1f0',
							border: '1px solid #fecaca',
							color: '#b91c1c',
							borderRadius: 999,
							padding: '4px 14px',
							fontSize: 13,
							fontWeight: 600,
							whiteSpace: 'nowrap',
						}}
					>
						<WarningFilled /> High Variance
					</span>
				)}
			</div>

			<Row gutter={32} align="stretch">
				<Col xs={24} lg={15}>
					{rows.map((row) => (
						<div
							key={row.key}
							style={{
								display: 'flex',
								justifyContent: SPACE_BETWEEN,
								alignItems: 'baseline',
								gap: 16,
								padding: '16px 0',
								borderBottom: '1px solid #f1f5f9',
							}}
						>
							<Text style={{ fontSize: 14, color: '#334155' }}>{row.label}</Text>
							<div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
								<Text
									strong
									style={{
										fontSize: 15,
										color: row.tone === 'error' ? '#dc2626' : '#0f172a',
									}}
								>
									{row.value}
								</Text>
								{row.note && (
									<Text style={{ fontSize: 13, color: '#94a3b8' }}>
										{' '}
										({row.note})
									</Text>
								)}
								{row.warning && (
									<Text
										style={{
											display: 'block',
											fontSize: 12,
											color: '#dc2626',
											whiteSpace: 'normal',
										}}
									>
										{row.warning}
									</Text>
								)}
							</div>
						</div>
					))}

					<div
						style={{
							display: 'flex',
							justifyContent: SPACE_BETWEEN,
							alignItems: 'center',
							gap: 16,
							padding: '16px 0 0 0',
						}}
					>
						<Text style={{ fontSize: 14, color: '#334155' }}>Threshold Status</Text>
						{thresholdPill ? (
							<span
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: 8,
									background: thresholdPill.background,
									color: thresholdPill.color,
									borderRadius: 999,
									padding: '4px 14px',
									fontSize: 13,
									fontWeight: 600,
								}}
							>
								<span
									style={{
										width: 7,
										height: 7,
										borderRadius: '50%',
										background: thresholdPill.color,
									}}
								/>
								{thresholdPill.label}
							</span>
						) : (
							<Text style={{ fontSize: 13, color: '#94a3b8' }}>
								Not assessable without a planned and a final distance
							</Text>
						)}
					</div>
				</Col>

				<Col xs={24} lg={9} style={{ marginTop: 16 }}>
					<DistanceSourceTagPanel model={model} />
				</Col>
			</Row>
		</Card>
	);
}

ReconciliationTable.propTypes = {
	model: PropTypes.object.isRequired,
};

export default ReconciliationTable;
